import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", description: "", price: 0, quote: false, quoteKind: "phone", quote_rules: "", quote_questions: "", duration_min: 30, active: true };

export default function Services() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/services").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", price: s.price ?? 0, quote: s.price == null, quoteKind: s.quote_rules ? "phone" : "onsite", quote_rules: s.quote_rules || "", quote_questions: s.quote_questions || "", duration_min: s.duration_min, active: s.active !== false });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { name: form.name, description: form.description, duration_min: form.duration_min, active: form.active, price: form.quote ? null : (parseFloat(form.price) || 0), quote_rules: form.quote && form.quoteKind === "phone" ? form.quote_rules : "", quote_questions: form.quote && form.quoteKind === "phone" ? form.quote_questions : "" };
      if (editing) { await api.patch(`/services/${editing.id}`, payload); toast.success("Service updated"); }
      else { await api.post("/services", payload); toast.success("Service added"); }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  const del = async (id) => { await api.delete(`/services/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Services</h1>
          <p className="text-slate-600 mt-1">What your business offers, with prices and durations.</p>
        </div>
        <Button data-testid="add-service-btn" onClick={openAdd} className="rounded-xl bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" />Add service</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price ($)</Label><Input data-testid="service-price-input" type="number" value={form.price} disabled={form.quote} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Duration (min)</Label><Input data-testid="service-duration-input" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} className="mt-1.5" /></div>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer" data-testid="service-quote-toggle">
              <input type="checkbox" className="mt-0.5" checked={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.checked })} />
              <span>No fixed price — the receptionist quotes each caller individually.</span>
            </label>
            {form.quote && (
              <div className="space-y-3 border border-slate-200 rounded-xl p-3" data-testid="quote-options">
                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer" data-testid="quote-kind-phone">
                  <input type="radio" className="mt-0.5" checked={form.quoteKind === "phone"} onChange={() => setForm({ ...form, quoteKind: "phone" })} />
                  <span><b>Quote over the phone</b> — the receptionist asks your questions and prices from your rules (e.g. cleaning, detailing).</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer" data-testid="quote-kind-onsite">
                  <input type="radio" className="mt-0.5" checked={form.quoteKind === "onsite"} onChange={() => setForm({ ...form, quoteKind: "onsite" })} />
                  <span><b>Quote on-site</b> — collect the address and dispatch a technician (e.g. plumbing, HVAC).</span>
                </label>
                {form.quoteKind === "phone" && (<>
                  <div>
                    <Label>Pricing rules</Label>
                    <textarea data-testid="service-quote-rules" value={form.quote_rules} onChange={(e) => setForm({ ...form, quote_rules: e.target.value })}
                      placeholder={"Light clean: up to 1,500 sq ft $120 · 1,500–2,500 sq ft $160\nDeep clean: up to 1,500 sq ft $220 · 1,500–2,500 sq ft $280"}
                      className="mt-1.5 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm min-h-[72px]" />
                  </div>
                  <div>
                    <Label>Info to collect for a quote</Label>
                    <textarea data-testid="service-quote-questions" value={form.quote_questions} onChange={(e) => setForm({ ...form, quote_questions: e.target.value })}
                      placeholder="Square footage, bedrooms, bathrooms, one-time or recurring, pets"
                      className="mt-1.5 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm min-h-[48px]" />
                  </div>
                  <p className="text-xs text-slate-400">If the rules don't cover a caller's answers, the receptionist saves their details and you call back with the quote.</p>
                </>)}
              </div>
            )}
            <p className="text-xs text-slate-500">ASFA uses this duration to block the right amount of time when booking.</p>
            <Button data-testid="service-save-btn" onClick={save} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700">{editing ? "Save changes" : "Save service"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <p>No services yet.</p>
            <p className="text-sm mt-1">Add services so ASFA can quote prices and book appointments.</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[520px]" data-testid="services-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-6 py-3">Service</th><th className="text-left px-6 py-3">Price</th><th className="text-left px-6 py-3">Duration</th><th className="px-6 py-3"></th></tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-6 py-4"><div className="font-medium">{s.name}</div><div className="text-xs text-slate-500">{s.description}</div></td>
                  <td className="px-6 py-4">{s.price == null ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100" data-testid={`service-quote-badge-${s.id}`}>{s.quote_rules ? "Quote by phone" : "Quote on-site"}</span> : `$${s.price.toFixed(2)}`}</td>
                  <td className="px-6 py-4">{s.duration_min} min</td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <button data-testid={`service-edit-${s.id}`} onClick={() => openEdit(s)} className="text-slate-400 hover:text-blue-600 mr-3"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`service-delete-${s.id}`} onClick={() => del(s.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
